/**
 * Layer 2 - Browse. Four ways into one dataset, as equal peers: month, store,
 * category, product - plus the receipts list itself. No forced drill order
 * (BUILD-SPEC 8); the axis is a segmented control, not a hierarchy.
 *
 * Every row carries its comparison, not just its total: months their delta,
 * stores and categories their share, products their latest price against
 * their average. All of it read off `summary.json`; the only operations in
 * this file are filter, sort and format.
 */
import { useMemo, useState } from "preact/hooks";

import type { Summary } from "./contracts/index.js";
import { categoryColor, dateName, money, monthName, pct } from "./format.js";
import { CategorySplit, DeltaChip, ShareBar } from "./widgets.js";

export type Axis = "months" | "stores" | "categories" | "products" | "receipts";

export interface BrowseNav {
  openReceipt: (id: string) => void;
  openProduct: (name: string) => void;
  openStore: (name: string) => void;
  openQueue: () => void;
}

const AXES: { id: Axis; label: string }[] = [
  { id: "months", label: "Months" },
  { id: "stores", label: "Stores" },
  { id: "categories", label: "Categories" },
  { id: "products", label: "Products" },
  { id: "receipts", label: "Receipts" },
];

export function Browse({
  summary,
  axis,
  onAxis,
  nav,
  queueDepth = 0,
}: {
  summary: Summary | null;
  axis: Axis;
  onAxis: (a: Axis) => void;
  nav: BrowseNav;
  /** Captures not yet in the ledger - shown atop Receipts, because "where is
   *  the one I photographed an hour ago" is answered by the queue, not here. */
  queueDepth?: number;
}) {
  if (!summary) {
    return (
      <div class="browse empty-state">
        <h2>Nothing published yet</h2>
        <p class="muted">
          Browse works on the published summary. Capture receipts, run the flow on your PC,
          and everything appears here.
        </p>
      </div>
    );
  }

  return (
    <div class="browse">
      <div class="seg" role="tablist" aria-label="Browse by">
        {AXES.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={axis === a.id}
            class={axis === a.id ? "on" : ""}
            onClick={() => onAxis(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {axis === "months" && <Months summary={summary} nav={nav} />}
      {axis === "stores" && <Stores summary={summary} nav={nav} />}
      {axis === "categories" && <Categories summary={summary} />}
      {axis === "products" && <Products summary={summary} nav={nav} />}
      {axis === "receipts" && <Receipts summary={summary} nav={nav} queueDepth={queueDepth} />}
    </div>
  );
}

// ---------------------------------------------------------------- months ---

function Months({ summary, nav }: { summary: Summary; nav: BrowseNav }) {
  const [open, setOpen] = useState<string | null>(null);
  const cur = summary.base_currency;
  const months = [...summary.months].reverse(); // newest first for a phone list

  return (
    <ul class="rows">
      {months.map((m) => (
        <li key={m.key} class={open === m.key ? "row open" : "row"}>
          <button
            type="button"
            class="rowmain"
            aria-expanded={open === m.key}
            onClick={() => setOpen(open === m.key ? null : m.key)}
          >
            <span class="title">{monthName(m.key)}</span>
            <span class="sub">{m.receipts} receipts</span>
            <span class="amount">{money(m.total, cur)}</span>
            <DeltaChip delta={m.delta} deltaPct={m.delta_pct} currency={cur} />
          </button>
          {open === m.key && (
            <div class="rowdetail">
              <CategorySplit categories={m.by_category} currency={cur} pct={m.by_category_pct} />
              <h4>Receipts</h4>
              <ul class="rows tight">
                {summary.receipts
                  .filter((r) => r.date.startsWith(m.key))
                  .map((r) => (
                    <li key={r.id} class="row">
                      <button type="button" class="rowmain" onClick={() => nav.openReceipt(r.id)}>
                        <span class="title">{r.store}</span>
                        <span class="sub">{dateName(r.date)}</span>
                        <span class="amount">{money(r.total, r.currency)}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- stores ---

function Stores({ summary, nav }: { summary: Summary; nav: BrowseNav }) {
  const cur = summary.base_currency;
  return (
    <ul class="rows">
      {summary.stores.map((s) => (
        <li key={s.name} class="row">
          <button type="button" class="rowmain" onClick={() => nav.openStore(s.name)}>
            <span class="title">{s.name}</span>
            <span class="sub">
              {s.visits} visits · {money(s.avg_per_visit, cur)} avg · last {dateName(s.last_visit)}
            </span>
            <span class="amount">{money(s.total, cur)}</span>
            <span class="share">
              <ShareBar share={s.share} />
              <span class="muted small">{pct(s.share)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------ categories ---

function Categories({ summary }: { summary: Summary }) {
  const cur = summary.base_currency;
  const rows = [...summary.categories].sort((a, b) => b.total - a.total);
  return (
    <ul class="rows">
      {rows.map((c) => (
        <li key={c.name} class="row">
          <div class="rowmain static">
            <span class="title">
              <span class="dot" style={{ background: categoryColor(c.name) }} /> {c.name}
            </span>
            <span class="sub">
              this month {money(c.month_total, cur)} · prior {money(c.prior_month_total, cur)}
            </span>
            <span class="amount">{money(c.total, cur)}</span>
            <span class="share">
              <ShareBar share={c.share} color={categoryColor(c.name)} />
              <span class="muted small">{pct(c.share)}</span>
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// -------------------------------------------------------------- products ---

function Products({ summary, nav }: { summary: Summary; nav: BrowseNav }) {
  const [filter, setFilter] = useState("");
  const rows = useMemo(() => {
    const sorted = [...summary.products].sort((a, b) => b.total_spent - a.total_spent);
    const q = filter.trim().toLowerCase();
    return q ? sorted.filter((p) => p.name.toLowerCase().includes(q)) : sorted;
  }, [summary, filter]);

  return (
    <>
      <input
        class="quickfilter"
        type="search"
        placeholder={`Filter ${summary.products.length} products…`}
        value={filter}
        onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
      />
      <ul class="rows">
        {rows.map((p) => (
          <li key={p.name} class="row">
            <button type="button" class="rowmain" onClick={() => nav.openProduct(p.name)}>
              <span class="title">
                <span class="dot" style={{ background: categoryColor(p.category) }} /> {p.name}
                {p.cross_currency && <span class="flag" title="prices span currencies">±</span>}
              </span>
              <span class="sub">
                {p.times_bought}× · avg {money(p.avg_each, summary.base_currency)} each
              </span>
              {p.latest && (
                <span class="amount">
                  {money(p.latest.each, p.latest.currency)}
                  <span class="muted small"> at {p.latest.store}</span>
                </span>
              )}
            </button>
          </li>
        ))}
        {!rows.length && <li class="muted pad">No product matches.</li>}
      </ul>
    </>
  );
}

// -------------------------------------------------------------- receipts ---

function Receipts({
  summary,
  nav,
  queueDepth,
}: {
  summary: Summary;
  nav: BrowseNav;
  queueDepth: number;
}) {
  return (
    <ul class="rows">
      {queueDepth > 0 && (
        <li class="row">
          <button type="button" class="rowmain qbanner" onClick={() => nav.openQueue()}>
            <span class="title">In the queue</span>
            <span class="sub">not in the ledger yet - captured, waiting for a run</span>
            <span class="amount">{queueDepth} ›</span>
          </button>
        </li>
      )}
      {summary.receipts.map((r) => (
        <li key={r.id} class="row">
          <button type="button" class="rowmain" onClick={() => nav.openReceipt(r.id)}>
            <span class="title">{r.store}</span>
            <span class="sub">
              {dateName(r.date)}
              {r.source === "pwa" ? " · phone" : ""}
            </span>
            <span class="amount">{money(r.total, r.currency)}</span>
            {r.discount > 0 && (
              <span class="delta down">saved {money(r.discount, r.currency)}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
